import React, { useState, useEffect } from 'react'
import { apiGet } from '../../utils/api'

interface ConversationInfoPanelProps {
  chatId: string
}

interface ChatMetrics {
  thread_id: string
  initiated_at: string
  department?: string
  first_agent?: string
  last_agent?: string
  current_agent?: string
  assignment_history?: { action: string; at: string }[]
  first_response_at?: string
  queue_in_at?: string
  queue_out_at?: string
  missed_at?: string
  missed_reason?: string
  source_ip?: string
  visitor_browser?: string
  visitor_os?: string
  landing_page?: string
  current_page?: string
  status: string
  duration_seconds?: number
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
}

function timeDiff(start: string, end: string): string {
  const diff = Math.floor((new Date(end).getTime() - new Date(start).getTime()) / 1000)
  return formatDuration(Math.abs(diff))
}

export const ConversationInfoPanel: React.FC<ConversationInfoPanelProps> = ({ chatId }) => {
  const [metrics, setMetrics] = useState<ChatMetrics | null>(null)
  const [expanded, setExpanded] = useState(true)

  useEffect(() => {
    apiGet<ChatMetrics>(`/chats/${chatId}/metrics`).then(setMetrics).catch(() => {})
  }, [chatId])

  if (!metrics) return null

  const items = [
    { label: 'Thread ID', value: metrics.thread_id },
    { label: 'Initiated At', value: metrics.initiated_at ? new Date(metrics.initiated_at).toLocaleString() : '-' },
    { label: 'Status', value: metrics.status },
    { label: 'Department', value: metrics.department || 'Unassigned' },
    { label: 'Handled By', value: metrics.current_agent || 'Unassigned' },
    { label: 'First Agent', value: metrics.first_agent || '-' },
    { label: 'Last Agent', value: metrics.last_agent || '-' },
    { label: 'First Response Time', value: metrics.first_response_at && metrics.initiated_at ? timeDiff(metrics.initiated_at, metrics.first_response_at) : '-' },
    { label: 'Queue Duration', value: metrics.queue_in_at && metrics.queue_out_at ? timeDiff(metrics.queue_in_at, metrics.queue_out_at) : '-' },
    { label: 'Missed At', value: metrics.missed_at ? new Date(metrics.missed_at).toLocaleString() : '-' },
    { label: 'Missed Reason', value: metrics.missed_reason || '-' },
    { label: 'Duration', value: metrics.duration_seconds ? formatDuration(metrics.duration_seconds) : '-' },
    { label: 'Visitor IP', value: metrics.source_ip || '-' },
    { label: 'Browser', value: metrics.visitor_browser || '-' },
    { label: 'OS', value: metrics.visitor_os || '-' },
    { label: 'Landing Page', value: metrics.landing_page || '-' },
    { label: 'Current Page', value: metrics.current_page || '-' },
  ]

  return (
    <div className="border-t border-border">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-txt-primary hover:bg-surface-hover transition-colors"
      >
        <span>Conversation Info</span>
        <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && (
        <div className="px-4 pb-3 space-y-1.5">
          {items.map((item, i) => (
            <div key={i} className="flex items-start justify-between gap-2">
              <span className="text-[11px] text-txt-tertiary shrink-0">{item.label}</span>
              <span className="text-[11px] text-txt-primary text-right truncate max-w-[180px]" title={String(item.value)}>
                {item.value}
              </span>
            </div>
          ))}

          {/* Assignment history — who picked the chat up and every transfer */}
          <div className="pt-2 mt-1 border-t border-border">
            <span className="text-[11px] font-semibold text-txt-secondary">Assignment History</span>
            {metrics.assignment_history && metrics.assignment_history.length > 0 ? (
              <ol className="mt-1.5 space-y-1.5">
                {metrics.assignment_history.map((ev, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-brand-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[11px] text-txt-primary leading-snug">{ev.action}</p>
                      <p className="text-[10px] text-txt-tertiary">{ev.at ? new Date(ev.at).toLocaleString() : ''}</p>
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="mt-1 text-[11px] text-txt-tertiary">No pickup or transfer recorded yet.</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
